import { AHText, TouchableBox } from "arkh";
import React from "react";
import { graphql, useFragment } from "react-relay";
import { openFileType } from "../useOpenFile";
import { FileThumbnail } from "./FileThumbnail";

import { SuggestionItemTitle } from "./SuggestionItemTitle";
import { useSuggestionItemLayout } from "./useSuggestionItemLayout";
import { SuggestionItem_File$key } from "./__generated__/SuggestionItem_File.graphql";
import {
  FileErrorReason,
  SuggestionItemStatusDetail_File$key,
} from "./__generated__/SuggestionItemStatusDetail_File.graphql";
import { Text } from "react-native";

export const SuggestionItem = React.memo(InnerSuggestionItem);

function InnerSuggestionItem({
  file,
  openFile,
}: {
  file: SuggestionItem_File$key;
  openFile: openFileType;
}) {
  const data = useFragment(
    graphql`
      fragment SuggestionItem_File on File {
        id
        name
        type

        pdfUrl
        status
        ...SuggestionItemStatusDetail_File
        ...FileThumbnail_file
      }
    `,
    file
  );

  const { width, height, size } = useSuggestionItemLayout();

  return (
    <TouchableBox
      onPress={() => {
        if (
          data.status === "READY" ||
          data.status === "PROCESSING" ||
          data.status === "WAIT_FOR_PROCESSING"
        ) {
          openFile({
            url: data.pdfUrl,
            fileId: data.id,
            fileName: data.name ?? "",
            fileType: data.type,
          });
        }
      }}
      hitSlop={{ left: 15, right: 15 }}
      style={{
        width,
        height,
        alignItems: "center",
      }}
      key={data.id}
    >
      <FileThumbnail size={size} file={data} />

      <SuggestionItemTitle>{data.name}</SuggestionItemTitle>
      <AHText type="detail" textAlign="center">
        <StatusDetail file={data} />
      </AHText>
    </TouchableBox>
  );
}

function reasonMessage(r: FileErrorReason): string {
  switch (r) {
    case "UNKNOWN":
      return "Något gick fel. Försök igen.";
    case "TOO_LARGE":
      return "Filen är för stor. Maximal filstorlek är 10MB.";
    case "CONVERSION_ERROR":
      return "Fel på fil. Kontrollera att filen är PDF, Word och Excel är tillåtna.";
  }

  return "";
}

function StatusDetail(props: { file: SuggestionItemStatusDetail_File$key }) {
  const data = useFragment(
    graphql`
      fragment SuggestionItemStatusDetail_File on File {
        status
        errorReason
      }
    `,
    props.file
  );

  switch (data.status) {
    case "READY":
      return null;
    case "PENDING":
      return <Text>Filen laddas upp</Text>;
    case "PROCESSING":
    case "WAIT_FOR_PROCESSING":
      return <Text>Analyseras...</Text>;
    case "ERROR":
      return <Text>Error: {reasonMessage(data.errorReason ?? "UNKNOWN")}</Text>;
  }

  return null;
}
